import { CssBaseline, type PaletteMode, useMediaQuery } from '@mui/material'
import {
	createTheme,
	StyledEngineProvider,
	ThemeProvider,
} from '@mui/material/styles'
import useLocalStorage from 'ply-utils/hooks/useLocalStorage'
import * as React from 'react'
import { getThemeOptions } from './theme'

const ColorModeContext = React.createContext<{
	mode: PaletteMode
	toggleColorMode: () => void
}>({
	mode: 'light',
	toggleColorMode: () => {
		return
	},
})

export function ColorModeProvider({ children }: React.PropsWithChildren) {
	const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
	const [mode, setMode] = useLocalStorage(
		'color-mode',
		prefersDarkMode ? 'dark' : 'light',
	) as [PaletteMode, (fn: (mode: PaletteMode) => PaletteMode) => void]
	const colorMode = React.useMemo(
		() => ({
			mode,
			toggleColorMode: () => {
				setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
			},
		}),
		[mode, setMode],
	)

	const theme = React.useMemo(() => createTheme(getThemeOptions(mode)), [mode])

	return (
		<ColorModeContext.Provider value={colorMode}>
			<StyledEngineProvider injectFirst>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					{children}
				</ThemeProvider>
			</StyledEngineProvider>
		</ColorModeContext.Provider>
	)
}

export function useColorMode() {
	return React.useContext(ColorModeContext)
}
